import { ApolloError } from "apollo-client";
import { Field, Form, Formik } from "formik";
import _ from "lodash";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ObjectSchema } from "yup";
import {
  Difference,
  getPrivacyLink,
  handlerErrors,
  standardizeGraphQLResult,
  toInputUppercase,
  UserProfileMutationPayloadFixer,
} from "../../../../helpers/helpers";
import { getUserProfile } from "../../../../reducers/auth";
import { RootState } from "../../../../rootReducer";
import ModalFieldInfo from "../../../common/ ModalFieldInfo/ModalFieldInfo";
import PasswordModalInfoMatch from "../../../common/PasswordModalInfoMatch/PasswordModalInfoMatch";
import PrivacyPolicy from "../../../common/PrivacyPolicy/PrivacyPolicy";
import Recaptcha from "../../../common/Recaptcha/Recaptcha";
import DeleteUserButton from "../DeleteUserButton/DeleteUserButton";
import { User } from "../model";
import {
  getSchemaUserCompany,
  getSchemaValidationCaptcha,
  getSchemaValidationPassword,
  initialValuesUserCompany,
} from "../schemaValidation";
import EmailChangeAlertModal from "../../../common/EmailChangeAlertModal/EmailChangeAlertModal";
import { showLoading, hideLoading } from "../../../../reducers/app";

export const CompanyRegistrationForm = (props: any) => {
  const dispatch = useDispatch();
  const privacyLink = getPrivacyLink("COMPANY_USER_REGISTRATION");
  let initValues = { ...props.initialValues };
  if (initValues) {
    initValues.confirmEmail = initValues.email;
    initValues.sameAsRegistrationEmail = true;
  }
  const initialValues = props.initialValues
    ? initValues
    : initialValuesUserCompany;
  const schemaValidation = props.initialValues
    ? getSchemaUserCompany()
    : (getSchemaUserCompany() as ObjectSchema<any>)
        .concat(getSchemaValidationPassword() as ObjectSchema<any>)
        .concat(getSchemaValidationCaptcha() as ObjectSchema<any>);
  const { onSubmit: mutation, t } = props;
  const action = props.initialValues ? "update" : "create";
  const [genericError, setGenericError] = useState(null);
  const [fieldInfo, setFieldInfo] = useState(false);
  const [fieldPass, setFieldPass] = useState(false);
  const [isContactEmailInfo, setIsContactEmailInfo] = useState(false);
  const [isAccessEmail, setIsAccessEmail] = useState(false);
  const [showEmailAlert, setShowEmailAlert] = useState(false);
  const [emailAlertShown, setEmailAlertShown] = useState(false);

  const handleAlertClose = () => {
    setShowEmailAlert(false);
    setEmailAlertShown(true);
  };

  const handleSubmit = async (
    initialValues: any,
    values: any,
    { setErrors }: { setErrors: any }
  ) => {
    if (action === "create" && values.sameAsRegistrationEmail) {
      values.contactEmail = values.email;
    }
    if (action === "update") {
      const finalEmail = values.email;
      const finalContactEmail = values.contactEmail;
      const emailChanged = finalEmail !== initialValues.email;
      const baselineContact = initialValues.contactEmail || initialValues.email;
      const contactEmailChanged = finalContactEmail !== baselineContact;
      const emailsDiffer = finalEmail !== finalContactEmail;
      const shouldAlert =
        (emailChanged || contactEmailChanged) &&
        emailsDiffer &&
        !emailAlertShown;
      if (shouldAlert) {
        setShowEmailAlert(true);
        props.setSubmitted(false);
        return;
      }
    }
    const changed =
      action === "update"
        ? !_.isEmpty(Difference(values, initialValues))
        : false;
    const formData = User.userCompany(
      action === "update" ? Difference(values, initialValues) : values,
      changed
    );
    props.setSubmitted(true);
    try {
      dispatch(showLoading());
      const result = await mutation(UserProfileMutationPayloadFixer(action, formData));
      await standardizeGraphQLResult(result, action);
      dispatch(hideLoading());
      props.setIsOpen(true);
      action === "create"
        ? props.setMessageConfirm("create_user_message")
        : props.setMessageConfirm("update_user_message");
      action === "update" && dispatch(getUserProfile());
    } catch (e: any) {
      dispatch(hideLoading());
      handlerErrors(e, setErrors, setGenericError);
    }
  };
  const [isRevealPwd, setIsRevealPwd] = useState(false);
  const [isRevealPwdConfirm, setIsRevealPwdConfirm] = useState(false);
  const loggedWith = useSelector((state: RootState) => state.auth.auth_issuer);
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={schemaValidation}
      onSubmit={(value, props) => handleSubmit(initialValues, value, props)}
      key={"PrivateRegistrationForm"}
      data-testid={"PrivateRegistrationForm"}
    >
      {({ errors, touched, values, setFieldValue, submitCount }) => (
        <Form autoComplete="off" role="presentation" noValidate>
          <div className="row CivilForm CompanyRegistrationForm">
            <div className="col-sm-12 col-md-10 offset-md-1">
              <div className={"row mt-5 mb-3"}>
                <div className={"col-lg-12 col-md-12"}>
                  <h2>{action === "create" ? t("registration.section.access") : t("data.modify.condominium.admin.login.credentials")}</h2>
                  <input type={"hidden"} value={""} name={"role"} />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6 col-md-12">
                  <div
                    className={
                      errors.email && touched.email && submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label className="paragraph-small-fs9" htmlFor="email">
                      {t("business.email.label")}*
                      <button
                        type="button"
                        className={"form-info-button"}
                        aria-label={t("aria.form.info.button")}
                        aria-haspopup="dialog"
                        aria-controls={"email-info-modal"}
                        onClick={() => {
                          setFieldInfo(true);
                          setIsAccessEmail(true);
                        }}
                      >
                        <span
                          className="form-icon-info"
                          aria-hidden="true"
                        ></span>
                      </button>
                    </label>
                    <Field
                      name={"email"}
                      id="email"
                      type="email"
                      className={
                        errors.email && touched.email && submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      aria-describedby={
                        errors.email && touched.email && submitCount > 0
                          ? "email-error"
                          : null
                      }
                      placeholder={t("registration.email.placeholder")}
                      onCopy={(e: any) => e.preventDefault()}
                      onPaste={(e: any) => e.preventDefault()}
                      onChange={(e: any) => {
                        setFieldValue("email", e.target.value);
                      }}
                      onBlur={(e: any) => {
                        if (
                          action === "create" &&
                          values.sameAsRegistrationEmail
                        ) {
                          setFieldValue("contactEmail", e.target.value);
                        }
                      }}
                      required={true}
                      disabled={loggedWith === "SPID"}
                    />
                    {errors.email && touched.email && submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"email-error"}
                      >
                        {errors.email as string}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="col-lg-6 col-md-12">
                  <div
                    className={
                      errors.confirmEmail &&
                      touched.confirmEmail &&
                      submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label
                      className="paragraph-small-fs9"
                      htmlFor="confirmEmail"
                    >
                      {t("registration.emailConfirm.value")}*
                    </label>
                    <Field
                      name={"confirmEmail"}
                      type="email"
                      id="confirmEmail"
                      className={
                        errors.confirmEmail &&
                        touched.confirmEmail &&
                        submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      aria-describedby={
                        errors.confirmEmail &&
                        touched.confirmEmail &&
                        submitCount > 0
                          ? "confirmEmail-error"
                          : null
                      }
                      required={true}
                      placeholder={t("registration.email.placeholder")}
                      onPaste={(e: any) => e.preventDefault()}
                      onCopy={(e: any) => e.preventDefault()}
                      disabled={loggedWith === "SPID"}
                    />
                    {errors.confirmEmail &&
                    touched.confirmEmail &&
                    submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"confirmEmail-error"}
                      >
                        {errors.confirmEmail as string}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
              {!props.initialValues && (
                <div className="row">
                  <div className="col-lg-6 col-md-12">
                    <div
                      className={
                        errors.password && touched.password && submitCount > 0
                          ? "form-group has-error"
                          : "form-group"
                      }
                    >
                      <label className="paragraph-small-fs9" htmlFor="password">
                        {t("registration.password.value")}*
                        <button
                          type="button"
                          className={"form-info-button"}
                          aria-label={t("aria.form.info.button")}
                          aria-haspopup="dialog"
                          aria-controls={"password-info-modal"}
                          onClick={() => {
                            setFieldPass(true);
                          }}
                        >
                          <span
                            className="form-icon-info"
                            aria-hidden="true"
                          ></span>
                        </button>
                      </label>
                      <Field
                        autoComplete="new-password"
                        name={"password"}
                        id="password"
                        type={isRevealPwd ? "text" : "password"}
                        className={
                          errors.password && touched.password && submitCount > 0
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        aria-describedby={
                          errors.password && touched.password && submitCount > 0
                            ? "password-error"
                            : null
                        }
                        required={true}
                        placeholder={t("registration.password.placeholder")}
                      />
                      <button
                        id={
                          errors.password && touched.password && submitCount > 0
                            ? "invalid-password"
                            : "gc-icon-service-sopralluogo-visualizza-1-col"
                        }
                        type="button"
                        aria-label={
                          isRevealPwd
                            ? t("hide.password.btn.label")
                            : t("show.password.btn.label")
                        }
                        onClick={() =>
                          setIsRevealPwd((prevState) => !prevState)
                        }
                        className={
                          isRevealPwd
                            ? "gc-icon-service-sopralluogo-visualizza-1-col deleted-eye"
                            : "gc-icon-service-sopralluogo-visualizza-1-col"
                        }
                      ></button>
                      {errors.password &&
                      touched.password &&
                      submitCount > 0 ? (
                        <p
                          className="form-error-text paragraph-small-form"
                          role="alert"
                          id={"password-error"}
                        >
                          {errors.password as string}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <div
                      className={
                        errors.confirmPassword &&
                        touched.confirmPassword &&
                        submitCount > 0
                          ? "form-group has-error"
                          : "form-group"
                      }
                    >
                      <label
                        className="paragraph-small-fs9"
                        htmlFor="confirmPassword"
                      >
                        {t("registration.confirmPassword.value")}*
                      </label>
                      <Field
                        name={"confirmPassword"}
                        id="confirmPassword"
                        type={isRevealPwdConfirm ? "text" : "password"}
                        className={
                          errors.confirmPassword &&
                          touched.confirmPassword &&
                          submitCount > 0
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        placeholder={t("registration.confirmPassword.value")}
                        aria-describedby={
                          errors.confirmPassword &&
                          touched.confirmPassword &&
                          submitCount > 0
                            ? "confirmPassword-error"
                            : null
                        }
                        required={true}
                      />
                      <button
                        id={
                          errors.password && touched.password && submitCount > 0
                            ? "invalid-password"
                            : "gc-icon-service-sopralluogo-visualizza-1-col"
                        }
                        type="button"
                        aria-label={
                          isRevealPwdConfirm
                            ? t("hide.password.btn.label")
                            : t("show.password.btn.label")
                        }
                        onClick={() =>
                          setIsRevealPwdConfirm((prevState) => !prevState)
                        }
                        className={
                          isRevealPwdConfirm
                            ? "gc-icon-service-sopralluogo-visualizza-1-col deleted-eye"
                            : "gc-icon-service-sopralluogo-visualizza-1-col"
                        }
                      ></button>
                      {errors.confirmPassword &&
                      touched.confirmPassword &&
                      submitCount > 0 ? (
                        <p
                          className="form-error-text paragraph-small-form"
                          role="alert"
                          id={"confirmPassword-error"}
                        >
                          {errors.confirmPassword as string}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
              <div className={"row mt-5 mb-3"}>
                <div className={"col-lg-12 col-md-12"}>
                  <h2>{action === "create" ? t("registration.section.personal") : t("registration.header2_update")}</h2>
                  <input type={"hidden"} value={""} name={"role"} />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6 col-md-12">
                  <div
                    className={
                      errors.name && touched.name && submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label className="paragraph-small-fs9" htmlFor="name">
                      {t("business.name.label")}*
                    </label>
                    <Field
                      name={"name"}
                      type="text"
                      id="name"
                      className={
                        errors.name && touched.name && submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      aria-describedby={
                        errors.name && touched.name && submitCount > 0
                          ? "name-error"
                          : null
                      }
                      required={true}
                      placeholder={t("registration.name.placeholder")}
                      disabled={loggedWith === "SPID"}
                    />
                    {errors.name && touched.name && submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"name-error"}
                      >
                        {errors.name as string}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="col-lg-6 col-md-12">
                  <div
                    className={
                      errors.surname && touched.surname && submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label className="paragraph-small-fs9" htmlFor="surname">
                      {t("business.surname.label")}*
                    </label>
                    <Field
                      name={"surname"}
                      id="surname"
                      type="text"
                      aria-describedby={
                        errors.surname && touched.surname && submitCount > 0
                          ? "surname-error"
                          : null
                      }
                      required={true}
                      className={
                        errors.surname && touched.surname && submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      placeholder={t("registration.surname.placeholder")}
                      disabled={loggedWith === "SPID"}
                    />
                    {errors.surname && touched.surname && submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"surname-error"}
                      >
                        {errors.surname as string}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6 col-md-12">
                  <div
                    className={
                      errors.fiscalCode && touched.fiscalCode && submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label className="paragraph-small-fs9" htmlFor="fiscalCode">
                      {t("business.codFiscale.label")}*
                    </label>
                    <Field
                      name={"fiscalCode"}
                      id="fiscalCode"
                      type="text"
                      onInput={toInputUppercase}
                      className={
                        errors.fiscalCode &&
                        touched.fiscalCode &&
                        submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      aria-describedby={
                        errors.fiscalCode &&
                        touched.fiscalCode &&
                        submitCount > 0
                          ? "fiscalCode-error"
                          : null
                      }
                      required={true}
                      placeholder={t("registration.fiscalCode-Iva.placeholder")}
                      disabled={loggedWith === "SPID"}
                    />
                    {errors.fiscalCode &&
                    touched.fiscalCode &&
                    submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"fiscalCode-error"}
                      >
                        {errors.fiscalCode as string}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="col-lg-6 col-md-12">
                  <div
                    className={
                      errors.pec && touched.pec && submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label className="paragraph-small-fs9" htmlFor="pec">
                      {t("business.pec.label")}
                    </label>
                    <Field
                      name={"pec"}
                      id="pec"
                      type="email"
                      className={
                        errors.pec && touched.pec && submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      aria-describedby={
                        errors.pec && touched.pec && submitCount > 0
                          ? "pec-error"
                          : null
                      }
                      placeholder={t(
                        "registration.emailAddressPec.placeholder"
                      )}
                    />
                    {errors.pec && touched.pec && submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"pec-error"}
                      >
                        {errors.pec as string}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6 col-md-12">
                  <div
                    className={
                      errors.landlinePhoneNumber &&
                      touched.landlinePhoneNumber &&
                      submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label
                      className="paragraph-small-fs9"
                      htmlFor="landlinePhoneNumber"
                    >
                      {t("registration.phone1.value")}
                    </label>
                    <Field
                      name={"landlinePhoneNumber"}
                      type="tel"
                      id="landlinePhoneNumber"
                      className={
                        errors.landlinePhoneNumber &&
                        touched.landlinePhoneNumber &&
                        submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      aria-describedby={
                        errors.landlinePhoneNumber &&
                        touched.landlinePhoneNumber &&
                        submitCount > 0
                          ? "landlinePhoneNumber-error"
                          : null
                      }
                      placeholder={t("registration.phone1.placeholder")}
                    />
                    {errors.landlinePhoneNumber &&
                    touched.landlinePhoneNumber &&
                    submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"landlinePhoneNumber-error"}
                      >
                        {errors.landlinePhoneNumber as string}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="col-lg-6 col-md-12">
                  <div
                    className={
                      errors.mobilePhoneNumber &&
                      touched.mobilePhoneNumber &&
                      submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label
                      className="paragraph-small-fs9"
                      htmlFor="mobilePhoneNumber"
                    >
                      {t("registration.phone2.value")}*
                    </label>
                    <Field
                      name={"mobilePhoneNumber"}
                      type="tel"
                      id="mobilePhoneNumber"
                      className={
                        errors.mobilePhoneNumber &&
                        touched.mobilePhoneNumber &&
                        submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      aria-describedby={
                        errors.mobilePhoneNumber &&
                        touched.mobilePhoneNumber &&
                        submitCount > 0
                          ? "mobilePhoneNumber-error"
                          : null
                      }
                      required={true}
                      placeholder={t("registration.phone2.placeholder")}
                    />
                    {errors.mobilePhoneNumber &&
                    touched.mobilePhoneNumber &&
                    submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"mobilePhoneNumber-error"}
                      >
                        {errors.mobilePhoneNumber as string}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
              {action === "create" && (
                <div className="row">
                  <div className="col-lg-6 col-md-12">
                    <div className="form-group checkbox-group">
                      <label className="paragraph-small-fs9">
                        {t("registration.contactEmail.value")}
                      </label>
                      <div className="form-check abc-checkbox">
                        <Field
                          type="checkbox"
                          name="sameAsRegistrationEmail"
                          id="sameAsRegistrationEmail"
                          className="form-check-input"
                          onChange={(e: any) => {
                            const checked = e.target.checked;
                            setFieldValue("sameAsRegistrationEmail", checked);
                            setFieldValue(
                              "contactEmail",
                              checked ? values.email : ""
                            );
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="sameAsRegistrationEmail"
                        >
                          {t("registration.contactEmail.sameAsRegistration")}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <div
                      className={
                        errors.contactEmail &&
                        touched.contactEmail &&
                        submitCount > 0
                          ? "form-group has-error"
                          : "form-group"
                      }
                    >
                      <label
                        className="paragraph-small-fs9"
                        htmlFor="contactEmail"
                      >
                        {t("registration.contactEmail.value")}*
                        <button
                          type="button"
                          className={"form-info-button"}
                          aria-label={t("aria.form.info.button")}
                          aria-haspopup="dialog"
                          aria-controls={"contact-email-info-modal"}
                          onClick={() => {
                            setFieldInfo(true);
                            setIsContactEmailInfo(true);
                          }}
                        >
                          <span
                            className="form-icon-info"
                            aria-hidden="true"
                          ></span>
                        </button>
                      </label>
                      <Field
                        name={"contactEmail"}
                        id="contactEmail"
                        type="email"
                        className={
                          errors.contactEmail &&
                          touched.contactEmail &&
                          submitCount > 0
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        aria-describedby={
                          errors.contactEmail &&
                          touched.contactEmail &&
                          submitCount > 0
                            ? "contactEmail-error"
                            : null
                        }
                        required={true}
                        placeholder={t("registration.contactEmail.placeholder")}
                        disabled={
                          values.sameAsRegistrationEmail ||
                          loggedWith === "SPID"
                        }
                      />
                      {errors.contactEmail &&
                      touched.contactEmail &&
                      submitCount > 0 ? (
                        <p
                          className="form-error-text paragraph-small-form"
                          role="alert"
                          id={"contactEmail-error"}
                        >
                          {errors.contactEmail as string}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
              {action === "update" && (
                <div className="row">
                  <div className="col-lg-6 col-md-12">
                    <div
                      className={
                        errors.contactEmail &&
                        touched.contactEmail &&
                        submitCount > 0
                          ? "form-group has-error"
                          : "form-group"
                      }
                    >
                      <label
                        className="paragraph-small-fs9"
                        htmlFor="contactEmail"
                      >
                        {t("registration.contactEmail.value")}*
                        <button
                          type="button"
                          className={"form-info-button"}
                          aria-label={t("aria.form.info.button")}
                          aria-haspopup="dialog"
                          aria-controls={"contact-email-info-modal"}
                          onClick={() => {
                            setFieldInfo(true);
                            setIsContactEmailInfo(true);
                          }}
                        >
                          <span
                            className="form-icon-info"
                            aria-hidden="true"
                          ></span>
                        </button>
                      </label>
                      <Field
                        name={"contactEmail"}
                        id="contactEmail"
                        type="email"
                        className={
                          errors.contactEmail &&
                          touched.contactEmail &&
                          submitCount > 0
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        aria-describedby={
                          errors.contactEmail &&
                          touched.contactEmail &&
                          submitCount > 0
                            ? "contactEmail-error"
                            : null
                        }
                        placeholder={t("registration.contactEmail.placeholder")}
                        disabled={loggedWith === "SPID"}
                      />
                      {errors.contactEmail &&
                      touched.contactEmail &&
                      submitCount > 0 ? (
                        <p
                          className="form-error-text paragraph-small-form"
                          role="alert"
                          id={"contactEmail-error"}
                        >
                          {errors.contactEmail as string}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {genericError && (
              <div className={"row"}>
                <div className={"col-sm-12 col-md-10 offset-md-1"}>
                  <div className={"generic-error"}>{genericError}</div>
                </div>
              </div>
            )}
          </div>
          <div className={"row"}>
            <div className="col-sm-12 col-md-10 offset-md-1">
              <div className="row">
                <div className="col-lg-12 col-md-12 mb-5 mt-5">
                  <h2 className="mb-4">
                    {t("form.privacy.label.title_third_scenario")}
                  </h2>
                  <div className="text-header">
                    <div>
                      <PrivacyPolicy
                        privacyLink={privacyLink}
                        privacyCtaLabel={
                          action === "create"
                            ? t("common.registerBtn")
                            : t("common.updateBtn")
                        }
                        thirdScenario
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={"row"}>
            <div className="col-sm-12 col-md-10 offset-md-1">
              <div className="row">
                <div
                  className={"col-lg-12 col-md-12 form-group checkbox-group"}
                >
                  <div className="form-check abc-checkbox">
                    <Field
                      name={"agreementMarketing"}
                      type="checkbox"
                      className={
                        errors.agreementMarketing &&
                        touched.agreementMarketing &&
                        submitCount > 0
                          ? "form-check-input is-invalid"
                          : "form-check-input"
                      }
                      id={"agreementMarketing"}
                      aria-describedby={
                        errors.agreementMarketing &&
                        touched.agreementMarketing &&
                        submitCount > 0
                          ? "agreementMarketing-error"
                          : null
                      }
                    />
                    <label
                      className={"form-check-label"}
                      htmlFor="agreementMarketing"
                    >
                      {t("form.privacy.label.first_checkbox")}
                    </label>
                    <div className={"form-check-label"}>
                      {t("form.privacy.label.optional")}
                    </div>
                    {errors.agreementMarketing &&
                    touched.agreementMarketing &&
                    submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"agreementMarketing-error"}
                      >
                        {errors.agreementMarketing as string}
                      </p>
                    ) : null}
                  </div>
                  <div className="form-check abc-checkbox">
                    <Field
                      name={"agreementProfiling"}
                      type="checkbox"
                      className={
                        errors.agreementProfiling &&
                        touched.agreementProfiling &&
                        submitCount > 0
                          ? "form-check-input is-invalid"
                          : "form-check-input"
                      }
                      id={"agreementProfiling"}
                      aria-describedby={
                        errors.agreementProfiling &&
                        touched.agreementProfiling &&
                        submitCount > 0
                          ? "agreementProfiling-error"
                          : null
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="agreementProfiling"
                    >
                      {t("form.privacy.label.second_checkbox")}
                    </label>
                    <div className="form-check-label">
                      {t("form.privacy.label.optional")}
                    </div>
                    {errors.agreementProfiling &&
                    touched.agreementProfiling &&
                    submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"agreementProfiling-error"}
                      >
                        {errors.agreementProfiling as string}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!props.initialValues && (
            <div className={"row"}>
              <div className="col-sm-12 col-md-10 offset-md-1">
                <div className="row">
                  <div className={"col-lg-12 col-md-12"}>
                    <Recaptcha type="recaptcha" name="recaptcha" />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={"row footer-form-actions"}>
            <div
              className={"col-sm-12 col-md-10 offset-md-1 btn-position"}
              style={{ paddingRight: "17px" }}
            >
              <button
                onClick={() => props.setSubmitted(true)}
                type="submit"
                className={"btn btn-primary"}
              >
                {action === "create"
                  ? t("common.registerBtn")
                  : t("common.updateBtn")}
              </button>
            </div>
          </div>
          <EmailChangeAlertModal
            show={showEmailAlert}
            onClose={handleAlertClose}
          />
          {fieldInfo ? (
            <ModalFieldInfo
              setFieldInfo={setFieldInfo}
              isLegacy={props.isLegacy}
              password={fieldPass}
              contactEmailInfo={isContactEmailInfo}
              setIsContactEmailInfo={setIsContactEmailInfo}
              condAdminEmailReg={isAccessEmail}
              setIsCondAdminEmailReg={setIsAccessEmail}
            />
          ) : null}
          {fieldPass ? (
            <PasswordModalInfoMatch
              show={fieldPass}
              onClose={() => setFieldPass(false)}
            />
          ) : null}
          <DeleteUserButton />
        </Form>
      )}
    </Formik>
  );
};
